import { useState, useEffect } from "react";
import axios from "axios";
import PropertyCard from "@/components/PropertyCard";
import { Search, SlidersHorizontal } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Properties() {
  const [listings, setListings] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ bedrooms: "", sleeps: "", featured: false });

  useEffect(() => {
    axios.get(`${API}/listings`).then(res => {
      setListings(res.data);
      setFiltered(res.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    let result = [...listings];
    if (filters.bedrooms) result = result.filter(l => l.bedrooms >= parseInt(filters.bedrooms));
    if (filters.sleeps) result = result.filter(l => l.sleeps >= parseInt(filters.sleeps));
    if (filters.featured) result = result.filter(l => l.is_featured);
    setFiltered(result);
  }, [filters, listings]);

  return (
    <div data-testid="properties-page" className="pt-20 min-h-screen bg-white">
      {/* Header */}
      <section className="px-6 md:px-12 lg:px-24 py-16 border-b border-[#E2E8F0]">
        <p className="text-xs uppercase tracking-[0.3em] text-[#32A334] mb-3">Our Collection</p>
        <h1 className="font-['DM_Serif_Display'] text-5xl sm:text-6xl tracking-tight text-[#1A1A2E] mb-4">
          Properties
        </h1>
        <p className="text-[#64748B] text-base max-w-xl">
          Browse our collection of premium vacation rental properties.
        </p>
      </section>

      {/* Filters */}
      <section className="px-6 md:px-12 lg:px-24 py-8 border-b border-[#E2E8F0] bg-[#F4F6FA]">
        <div className="flex flex-wrap items-center gap-6" data-testid="property-filters">
          <div className="flex items-center gap-2 text-[#64748B]">
            <SlidersHorizontal className="w-4 h-4" />
            <span className="text-xs uppercase tracking-[0.15em]">Filter</span>
          </div>
          <select
            value={filters.bedrooms}
            onChange={(e) => setFilters(f => ({ ...f, bedrooms: e.target.value }))}
            data-testid="filter-bedrooms"
            className="bg-white border border-[#E2E8F0] focus:border-[#32A334] text-[#1A1A2E] py-2 px-3 text-sm outline-none cursor-pointer rounded"
          >
            <option value="">Bedrooms</option>
            <option value="1">1+</option>
            <option value="2">2+</option>
            <option value="3">3+</option>
            <option value="4">4+</option>
          </select>
          <select
            value={filters.sleeps}
            onChange={(e) => setFilters(f => ({ ...f, sleeps: e.target.value }))}
            data-testid="filter-sleeps"
            className="bg-white border border-[#E2E8F0] focus:border-[#32A334] text-[#1A1A2E] py-2 px-3 text-sm outline-none cursor-pointer rounded"
          >
            <option value="">Sleeps</option>
            <option value="4">4+</option>
            <option value="6">6+</option>
            <option value="8">8+</option>
            <option value="10">10+</option>
          </select>
          <label className="flex items-center gap-2 cursor-pointer text-sm text-[#64748B]" data-testid="filter-featured">
            <input
              type="checkbox"
              checked={filters.featured}
              onChange={(e) => setFilters(f => ({ ...f, featured: e.target.checked }))}
              className="accent-[#32A334]"
            />
            Featured Only
          </label>
          {(filters.bedrooms || filters.sleeps || filters.featured) && (
            <button
              onClick={() => setFilters({ bedrooms: "", sleeps: "", featured: false })}
              data-testid="clear-filters"
              className="text-xs text-[#32A334] hover:text-[#1C671D] uppercase tracking-[0.15em] transition-colors"
            >
              Clear Filters
            </button>
          )}
          <span className="ml-auto text-xs text-[#94A3B8]">{filtered.length} {filtered.length === 1 ? 'property' : 'properties'}</span>
        </div>
      </section>

      {/* Listings Grid */}
      <section className="px-6 md:px-12 lg:px-24 py-16">
        {loading ? (
          <div className="flex justify-center py-24">
            <div className="w-8 h-8 border-2 border-[#32A334] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-24">
            <Search className="w-12 h-12 text-[#94A3B8] mx-auto mb-4" />
            <p className="text-[#64748B] text-lg">No properties match your filters.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="listings-grid">
            {filtered.map(listing => (
              <PropertyCard key={listing.id} listing={listing} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
